import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { getDateRange, getCurrentRangeType } from '../utils/Invoices.dateRange.utils';
import { fetchCompanyFilters, getFilterSellers } from '../../../../store/actions/rateCard';
import { getAllSellersList } from '../../../../store/actions';
import { fetchInvoiceGroupHeader } from '../../../../store/actions/invoice.action';
import { getSelectList } from '../../../../config/utils';
import { computeAutoSelectedSellers } from '../utils/Invoices.autoSelect.utils';

// ============================================================================
// Custom Hooks - Extracted Business Logic
// ============================================================================

/**
 * useFilterData - Manages filter state and initialization
 * Consolidated state matching EditUserManagement pattern
 * @returns {Object} { filterData, setfilterData, defaultFilterData }
 */
export const useFilterData = () => {
    // Get "This Month" date range for defaults
    const [thisMonthStart, thisMonthEnd] = getDateRange('This Month');

    const defaultFilterData = {
        // Pagination & Sorting
        pageIndex: 1,
        pageSize: 30,
        totalPages: 1,
        sortColumn: '',
        sortOrder: 'DESCENDING',
        
        // Search & Filter
        searchText: '',
        statusIdList: [],
        
        // Company & Seller Selection
        companyIdList: [],
        sellerIdList: [],
        defaultCompanyId: null,
        defaultSellerId: null,
        
        // Date Range
        dateFrom: thisMonthStart,
        dateTo: thisMonthEnd,
        
        // UI State
        showFilter: false,
        showSearch: false,
    };

    const [filterData, setfilterData] = useState(defaultFilterData);

    return { filterData, setfilterData, defaultFilterData };
};

/**
 * useDateRangeFilters - Manages date range state and handlers
 * @param {Object} filterData - Current filter data
 * @param {Function} setfilterData - Set filter data function
 * @returns {Object} { selectedDateRange, setSelectedDateRange, handleDateRangeFilter, handleDateFieldChange }
 */
export const useDateRangeFilters = (filterData, setfilterData) => {
    const [selectedDateRange, setSelectedDateRange] = useState('This Month');

    const handleDateRangeFilter = rangeType => {
        setSelectedDateRange(rangeType);

        if (rangeType === 'Custom Range') {
            setfilterData({
                ...filterData,
                dateFrom: '',
                dateTo: '',
                pageIndex: 1,
                showFilter: true,
            });
        } else {
            const [dateFrom, dateTo] = getDateRange(rangeType);
            setfilterData({
                ...filterData,
                dateFrom,
                dateTo,
                pageIndex: 1,
                showFilter: true,
            });
        }
    };

    const handleDateFieldChange = (date, fieldName) => {
        const updatedFilterData = { ...filterData, [fieldName]: date };
        setfilterData(updatedFilterData);

        // Auto-detect range based on updated dates
        if (updatedFilterData.dateFrom && updatedFilterData.dateTo) {
            const matchedRange = getCurrentRangeType(updatedFilterData.dateFrom, updatedFilterData.dateTo);
            if (matchedRange) {
                setSelectedDateRange(matchedRange);
            } else {
                setSelectedDateRange('Custom Range');
            }
        }
    };

    return { selectedDateRange, setSelectedDateRange, handleDateRangeFilter, handleDateFieldChange };
};

/**
 * useCompanySellerFilters - Manages company and seller data fetching and filtering with auto-select
 * Simplified pattern matching EditUserManagement approach
 * @param {Function} dispatch - Redux dispatch
 * @returns {Object} { companyList, sellerList, allSellers, handleMultiSelect, performAutoSelectSeller }
 */
export const useCompanySellerFilters = (dispatch) => {
    const { filterCompanies, filterAllSellers } = useSelector(state => state.RateCardReducer);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);

    // Fetch company and initial seller data on mount
    useEffect(() => {
        const companyData = {
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ascending',
        };
        const sellerData = {
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ASCENDING',
            companyIdList: [0],
            screenType: 2,
        };
        dispatch(fetchCompanyFilters(companyData));
        dispatch(getFilterSellers(sellerData));
    }, [dispatch]);

    // Populate company and seller lists from Redux
    useEffect(() => {
        const companyListData = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        const sellerListData = filterAllSellers?.EntityList || [];
        
        if (companyListData.length) {
            setCompanyList(getSelectList(companyListData, 'Id', 'name'));
        }
        if (sellerListData.length) {
            const formattedSellers = getSelectList(sellerListData, 'id', 'sellerName');
            setAllSellers(formattedSellers);
            setSellerList(formattedSellers);
        }
    }, [filterCompanies, filterAllSellers]);

    // Fetch sellers for selected companies
    const fetchSellers = async (companyListIds) => {
        if (companyListIds.length === 0) {
            setSellerList(allSellers);
            return;
        }
        
        try {
            const response = await getAllSellersList({ companyIds: companyListIds });
            if (response?.data?.Status === 200) {
                const dataList = response?.data?.Entity?.listSellerMasterOrdersEntity?.map(item => ({
                    ...item,
                    label: item.sellerAndCompany,
                    value: item.id,
                })) || [];
                setSellerList(dataList);
            }
        } catch (err) {
            console.error('Error fetching sellers:', err);
            setSellerList([]);
        }
    };

    // Handle company/seller selection changes
    const handleMultiSelect = (value, fieldName, filterData, setfilterData) => {
        if (fieldName === 'companyIdList') {
            const companyIds = _.map(value, 'Id');
            
            // Clear seller selections when company changes
            // This allows auto-select to trigger fresh when new sellers arrive
            setfilterData({
                ...filterData,
                companyIdList: value,
                sellerIdList: [], // Clear sellers for fresh auto-select
            });
            
            // Fetch sellers for new company selection
            fetchSellers(companyIds);
        } else {
            // Non-company field changes (e.g., seller selection)
            setfilterData({
                ...filterData,
                [fieldName]: value,
            });
        }
    };

    // Auto-select sellers when list updates after company selection
    // Uses utility function for idempotent auto-selection
    const performAutoSelectSeller = (filterData, setfilterData) => {
        if (!filterData.companyIdList?.length || !Array.isArray(sellerList)) {
            return;
        }

        // Only auto-select if seller field is empty
        if (filterData.sellerIdList && filterData.sellerIdList.length > 0) {
            return;
        }

        // Use utility to intelligently auto-select sellers
        const autoSelectedSellers = computeAutoSelectedSellers(
            sellerList,
            filterData.companyIdList,
            []
        );

        if (autoSelectedSellers.length > 0) {
            setfilterData({
                ...filterData,
                sellerIdList: autoSelectedSellers,
            });
        }
    };

    return { 
        companyList, 
        sellerList, 
        allSellers, 
        handleMultiSelect, 
        performAutoSelectSeller,
    };
};

/**
 * useInvoiceData - Manages invoice Redux state and API calls
 * @returns {Object} { invoiceList, invoiceListStatus, totalRecords, getAllInvoices }
 */
export const useInvoiceData = (filterData) => {
    const dispatch = useDispatch();
    const { invoiceList, invoiceListStatus, totalRecords } = useSelector(state => state.invoiceReducer);

    const getAllInvoices = () => {
        let data = {
            pageIndex: filterData.pageIndex,
            pageSize: filterData.pageSize,
            sortColumn: filterData.sortColumn,
            sortOrder: filterData.sortOrder,
            dateFrom: filterData.dateFrom,
            dateTo: filterData.dateTo,
            searchText: filterData.searchText?.trim(),
            sellerIdList: _.map(filterData.sellerIdList, 'id'),
            // companyIdList: [114],
            companyIdList: _.map(filterData.companyIdList, 'Id'),
        };
        dispatch(fetchInvoiceGroupHeader(data));
    };

    return { invoiceList, invoiceListStatus, totalRecords, getAllInvoices };
};
